import React, { useState, useEffect, useRef, useMemo } from 'react';
import { sanitizeHtml } from '../../utils/sanitize';
import { generateItineraryPDF } from '../../utils/pdfGenerator';
import {
  FaFilePdf, FaMapMarkerAlt, FaPlane, FaHeart, FaUtensils,
  FaCalendarAlt, FaCar, FaRoute, FaReceipt, FaDollarSign,
  FaCalculator, FaClipboardList, FaLandmark, FaGlobe,
  FaChevronDown, FaChevronRight, FaStar, FaClock, FaLock,
  FaCrown, FaCheck, FaTimes, FaPlus, FaTrash, FaEdit,
  FaTh, FaList, FaImage, FaArrowLeft, FaCheckSquare, FaRegSquare,
  FaInfoCircle, FaDirections, FaExchangeAlt, FaTree
} from 'react-icons/fa';
import DemoChatbot from './DemoChatbot';
import DemoExpenses from './DemoExpenses';
import DemoPhotoGallery from './DemoPhotoGallery';
import DemoFlightManager from './DemoFlightManager';
import { DayCardsGrid, DayDetailModal, extractDaysFromContent } from '../dashboard';
import { extractLocationsWithCoordinates } from '../../utils/itineraryParser';
import ReviewLinks from '../ReviewLinks';
import BookingBlock from '../BookingBlock';
import { toISODate } from '../../utils/tripContext';
import { usePlaceImage } from '@travel-planner/shared';

/**
 * Shift a sample's stored dates onto a rolling near-future window, keeping the
 * trip's length.
 *
 * The four samples are illustrative rather than real and their stored dates are
 * already in the past, so every per-night hotel link derived from them would
 * open a Booking.com search for a stay that cannot be booked — an empty results
 * page with no prices, which converts at nothing. Anchoring on the first Friday
 * more than 30 days out gives a plausible weekend break and keeps the dates
 * stable for a month at a time.
 */
function rollSampleDates(startDate, endDate) {
  const start = startDate ? new Date(`${startDate}T00:00:00`) : null;
  if (!start || isNaN(start.getTime())) return { startDate, endDate };

  const end = endDate ? new Date(`${endDate}T00:00:00`) : null;
  const lengthMs = end && !isNaN(end.getTime()) ? Math.max(0, end - start) : 0;

  const anchor = new Date();
  anchor.setHours(0, 0, 0, 0);
  anchor.setDate(anchor.getDate() + 30);
  // 5 = Friday; advance to the next one on or after the 30-day mark.
  anchor.setDate(anchor.getDate() + ((5 - anchor.getDay() + 7) % 7));

  return {
    startDate: toISODate(anchor),
    endDate: endDate ? toISODate(new Date(anchor.getTime() + lengthMs)) : endDate,
  };
}

// Category icon lookup for attractions
const CATEGORY_KEYWORDS = {
  restaurant: FaUtensils, cafe: FaUtensils, food: FaUtensils, dining: FaUtensils, ramen: FaUtensils,
  breakfast: FaUtensils, lunch: FaUtensils, dinner: FaUtensils, brunch: FaUtensils, meal: FaUtensils,
  museum: FaLandmark, palace: FaLandmark, temple: FaLandmark, cathedral: FaLandmark, castle: FaLandmark,
  shrine: FaLandmark, monument: FaLandmark, church: FaLandmark, mosque: FaLandmark, basilica: FaLandmark,
  park: FaTree, garden: FaTree, forest: FaTree, nature: FaTree, beach: FaTree, trail: FaTree, terrace: FaTree,
};

function getCategoryIcon(name) {
  const lower = name.toLowerCase();
  for (const [keyword, Icon] of Object.entries(CATEGORY_KEYWORDS)) {
    if (lower.includes(keyword)) return Icon;
  }
  return FaMapMarkerAlt;
}

// Parse attractions with coordinates from itinerary content
function parseAttractionsFromContent(content) {
  if (!content) return [];
  const lines = content.split('\n');
  const attractions = [];
  const coordPattern = /\*\*([^*]+)\*\*(?:\s*\([^)]*\))*\s*(?:Coordinates:\s*)?\((-?\d{1,3}\.\d+),\s*(-?\d{1,3}\.\d+)\)/;
  let current = null;

  for (const line of lines) {
    const match = line.match(coordPattern);
    if (match) {
      if (current) attractions.push(current);
      current = { name: match[1].trim(), lat: parseFloat(match[2]), lng: parseFloat(match[3]), description: '' };
      continue;
    }
    if (!current) continue;
    const trimmed = line.trim();
    if (!trimmed) continue;
    const clean = trimmed.replace(/^[-*•]+\s*/, '');
    if (!current.description && !/^\*\*/.test(clean) && !/^\[/.test(clean) &&
        !/^distance/i.test(clean) && !/^information url/i.test(clean) &&
        !/^more info/i.test(clean) && clean.length > 20) {
      current.description = clean.replace(/\s+/g, ' ').trim();
    }
  }
  if (current) attractions.push(current);
  return attractions.filter(a => a.lat >= -90 && a.lat <= 90 && a.lng >= -180 && a.lng <= 180);
}

// Attraction card with image, links, add/replace
const DemoAttractionCard = ({ attraction, index, destination, onGetStarted, onAdd, onToggleReplace, isPickingReplace, itineraryDays, onReplace, isAdded, hasReachedLimit }) => {
  const IconComponent = getCategoryIcon(attraction.name);
  const imageUrl = usePlaceImage(attraction.name, attraction.lat, attraction.lng);

  return (
    <div>
      <div className="border border-neutral-200 rounded-xl p-4 hover:border-purple-300 hover:shadow-md transition-all">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1">
            <div className="flex items-start gap-3 mb-2">
              {imageUrl ? (
                <img src={imageUrl} alt={attraction.name} loading="lazy" className="w-[72px] h-[72px] rounded-lg object-cover flex-shrink-0" />
              ) : (
                <div className="w-[72px] h-[72px] bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <IconComponent className="text-purple-500 text-2xl" />
                </div>
              )}
              <div>
                <h4 className="font-semibold text-neutral-800">{attraction.name}</h4>
                {attraction.description && <p className="text-sm text-neutral-600 mt-0.5">{attraction.description}</p>}
                <p className="text-xs text-neutral-400 mt-1">{attraction.lat.toFixed(4)}, {attraction.lng.toFixed(4)}</p>
              </div>
            </div>

            {/* Action links */}
            <div className="flex items-center flex-wrap gap-2 mt-3">
              <a href={`https://www.google.com/search?q=${encodeURIComponent(attraction.name + (destination ? ' ' + destination : ''))}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-indigo-600 bg-indigo-50 hover:bg-indigo-100 rounded-lg transition-colors">
                <FaInfoCircle size={11} /> More Info
              </a>
              <a href={`https://www.google.com/maps/search/?api=1&query=${attraction.lat},${attraction.lng}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-sky-600 bg-sky-50 hover:bg-sky-100 rounded-lg transition-colors">
                <FaMapMarkerAlt size={11} /> View on Map
              </a>
              <a href={`https://www.google.com/maps/dir/?api=1&destination=${attraction.lat},${attraction.lng}`} target="_blank" rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-teal-600 bg-teal-50 hover:bg-teal-100 rounded-lg transition-colors">
                <FaDirections size={11} /> Directions
              </a>
              <ReviewLinks name={attraction.name} location={destination} coordinates={{ lat: attraction.lat, lng: attraction.lng }} />
            </div>
          </div>

          {/* Add / Replace buttons */}
          <div className="flex-shrink-0 flex flex-col gap-2">
            {isAdded ? (
              <span className="flex items-center gap-1 px-3 py-2 rounded-lg font-medium text-sm bg-green-100 text-green-700">
                <FaCheck className="text-xs" /> Added
              </span>
            ) : hasReachedLimit ? (
              <button
                onClick={() => onGetStarted && onGetStarted()}
                className="flex items-center gap-1 px-3 py-2 rounded-lg font-medium text-sm bg-neutral-200 text-neutral-500 hover:bg-neutral-300 transition-all"
              >
                <FaLock className="text-xs" /> Upgrade
              </button>
            ) : (
              <button
                onClick={() => onAdd(attraction, index)}
                className="flex items-center gap-1 px-3 py-2 rounded-lg font-medium text-sm bg-purple-500 text-white hover:bg-purple-600 shadow-sm hover:shadow-md transition-all"
              >
                <FaPlus className="text-xs" /> Add to Trip
              </button>
            )}
            <button
              onClick={() => {
                if (hasReachedLimit && !isAdded) {
                  onGetStarted && onGetStarted();
                } else {
                  onToggleReplace(index);
                }
              }}
              className={`flex items-center gap-1 px-3 py-2 rounded-lg font-medium text-sm transition-all border ${
                isPickingReplace
                  ? 'border-purple-400 bg-purple-50 text-purple-700'
                  : 'border-neutral-300 text-neutral-600 hover:border-purple-300 hover:text-purple-600'
              }`}
            >
              <FaExchangeAlt className="text-xs" /> Replace a Stop
            </button>
          </div>
        </div>
      </div>

      {/* Inline replace picker */}
      {isPickingReplace && itineraryDays && itineraryDays.length > 0 && (
        <div className="mt-3 border border-neutral-200 rounded-xl bg-white shadow-lg overflow-hidden">
          <div className="flex items-center justify-between px-4 py-3 bg-neutral-50 border-b border-neutral-100">
            <p className="text-sm text-neutral-600">
              Replace with <span className="font-semibold text-purple-600">{attraction.name}</span>
            </p>
            <button onClick={() => onToggleReplace(null)} className="text-neutral-400 hover:text-neutral-600 transition-colors p-1 rounded-md hover:bg-neutral-200">
              <FaTimes size={12} />
            </button>
          </div>
          <div className="max-h-72 overflow-y-auto divide-y divide-neutral-50">
            {itineraryDays.map((day) => {
              const stops = (day.locations || []).filter(loc => !loc.isAccommodation);
              if (stops.length === 0) return null;
              return (
                <div key={day.dayNumber || day.day} className="py-2 px-2">
                  <div className="bg-sky-50 rounded-lg px-3 py-1.5 mb-1">
                    <p className="text-xs font-semibold text-sky-700">
                      {day.dayTitle || `Day ${day.dayNumber || day.day}`}
                    </p>
                  </div>
                  {stops.map((loc, locIdx) => (
                    <button
                      key={locIdx}
                      onClick={() => onReplace(attraction, index, loc.name, day.dayNumber || day.day)}
                      className="w-full text-left flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm text-neutral-700 hover:bg-purple-50 hover:text-purple-700 transition-colors"
                    >
                      <FaMapMarkerAlt className="text-[10px] text-neutral-300" />
                      <span>{loc.name}</span>
                    </button>
                  ))}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Sample destinations config
const SAMPLE_SLUGS = {
  'tokyo-japan': { name: 'Tokyo, Japan', category: 'Cities', file: '/samples/tokyo-japan.json' },
  'bali-indonesia': { name: 'Bali, Indonesia', category: 'Beaches', file: '/samples/bali-indonesia.json' },
  'barcelona-spain': { name: 'Barcelona, Spain', category: 'Cities', file: '/samples/barcelona-spain.json' },
  'swiss-alps-switzerland': { name: 'Swiss Alps, Switzerland', category: 'Mountains', file: '/samples/swiss-alps-switzerland.json' },
};

// Parse content into main itinerary and checklist
const parseContentSections = (content) => {
  if (!content) return { mainContent: '', checklistContent: '' };
  const lines = content.split('\n');
  const mainLines = [];
  const checklistLines = [];
  let inChecklist = false;

  for (const line of lines) {
    if (/^#+\s*(Packing|Checklist)/i.test(line.trim())) {
      inChecklist = true;
    }
    if (inChecklist) {
      checklistLines.push(line);
    } else {
      mainLines.push(line);
    }
  }

  return {
    mainContent: mainLines.join('\n'),
    checklistContent: checklistLines.join('\n'),
  };
};

// Parse checklist content into sections
const parseChecklistIntoSections = (content) => {
  if (!content) return [];
  const lines = content.split('\n');
  const sections = [];
  let currentSection = null;

  for (const line of lines) {
    const sectionMatch = line.trim().match(/^#+\s*(.+)/);
    if (sectionMatch) {
      currentSection = { title: sectionMatch[1].replace(/\*\*/g, ''), items: [] };
      sections.push(currentSection);
      continue;
    }
    const checkMatch = line.trim().match(/^-\s*\[([ x])\]\s*(.*)/i);
    if (checkMatch && currentSection) {
      currentSection.items.push(checkMatch[2]);
    }
  }

  return sections;
};

// Inline formatted content renderer (simplified version)
const FormattedContent = ({ content }) => {
  if (!content) return null;
  const lines = content.split('\n');

  const formatLine = (line, index) => {
    const trimmed = line.trim();
    if (!trimmed || /^---+$/.test(trimmed)) return null;

    // Day header
    const dayMatch = trimmed.match(/^(?:#{1,3}\s*)?\*?\*?(?:Day|Part)\s*(\d+)[:\s\-,]*(.*?)(?:\*\*)?$/i);
    if (dayMatch) {
      return (
        <div key={index} className="mt-8 mb-4 first:mt-0">
          <div className="bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-xl p-4 shadow-md">
            <h3 className="text-xl font-bold flex items-center gap-2">
              <FaCalendarAlt /> Day {dayMatch[1]} {dayMatch[2] && `- ${dayMatch[2].replace(/\*\*/g, '').trim()}`}
            </h3>
          </div>
        </div>
      );
    }

    // Section headers
    if (trimmed.startsWith('##')) {
      return (
        <h3 key={index} className="text-lg font-bold text-neutral-800 mt-6 mb-3 pb-2 border-b-2 border-primary-500">
          {trimmed.replace(/^##\s*/, '')}
        </h3>
      );
    }

    // Time headers like "**Morning (9:00 AM):**"
    const timeMatch = trimmed.match(/^\*?\*?(Morning|Afternoon|Evening|Lunch|Dinner|Breakfast|Night)(\s*\([^)]*\))?:?\*?\*?:?$/i);
    if (timeMatch) {
      return (
        <div key={index} className="mt-5 mb-3">
          <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-secondary-100 text-secondary-700 rounded-full font-semibold text-sm uppercase tracking-wide">
            <FaClock className="text-xs" />
            {trimmed.replace(/\*\*/g, '').replace(/:$/, '')}
          </span>
        </div>
      );
    }

    // Numbered items with bold names
    const numberedMatch = trimmed.match(/^\d+\.\s+\*\*([^*]+)\*\*(?:\s*\([^)]+\))?\s*[-–—]?\s*(.*)/);
    if (numberedMatch) {
      const name = numberedMatch[1].trim();
      const desc = numberedMatch[2]?.replace(/\*\*/g, '').replace(/\[([^\]]*)\]\([^)]+\)/g, '$1').trim();
      return (
        <div key={index} className="ml-4 mb-4 p-4 bg-white rounded-lg border border-neutral-200 shadow-sm hover:shadow-md transition-shadow">
          <div className="flex items-start gap-3">
            <div className="w-8 h-8 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <FaMapMarkerAlt className="text-primary-600 text-sm" />
            </div>
            <div>
              <h4 className="font-bold text-neutral-800">{name}</h4>
              {desc && <p className="text-neutral-600 text-sm mt-1">{desc}</p>}
            </div>
          </div>
        </div>
      );
    }

    // Bullet points
    const bulletMatch = trimmed.match(/^\s*[*•-]\s+(.*)/);
    if (bulletMatch) {
      const text = bulletMatch[1].replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\[([^\]]*)\]\([^)]+\)/g, '$1');
      return (
        <div key={index} className="flex items-start gap-2 ml-8 mb-1.5">
          <span className="w-1.5 h-1.5 bg-accent-400 rounded-full mt-2 flex-shrink-0"></span>
          <span className="text-neutral-600 text-sm" dangerouslySetInnerHTML={{ __html: sanitizeHtml(text) }}></span>
        </div>
      );
    }

    // Checklist items
    const checkMatch = trimmed.match(/^-\s*\[([ x])\]\s*(.*)/i);
    if (checkMatch) return null; // Handled separately

    // Regular text
    if (trimmed.length > 3) {
      const formatted = trimmed.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>');
      return (
        <p key={index} className="text-neutral-700 mb-2 ml-4" dangerouslySetInnerHTML={{ __html: sanitizeHtml(formatted) }}></p>
      );
    }

    return null;
  };

  return (
    <div className="prose prose-neutral max-w-none">
      {lines.map((line, index) => formatLine(line, index)).filter(Boolean)}
    </div>
  );
};

const DEMO_ATTRACTION_ADD_LIMIT = 2;

const DemoAttractionsTab = ({ content, destination, onGetStarted, itineraryDays }) => {
  const [addedCount, setAddedCount] = useState(0);
  const [addedIndices, setAddedIndices] = useState(new Set());
  const [replacingIndex, setReplacingIndex] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  const attractions = useMemo(() => {
    if (!content) return [];
    const parsed = parseAttractionsFromContent(content);
    if (parsed.length > 0) return parsed;
    return extractLocationsWithCoordinates(content);
  }, [content]);

  const hasReachedLimit = addedCount >= DEMO_ATTRACTION_ADD_LIMIT;

  const handleAdd = (attraction, index) => {
    if (hasReachedLimit) return;
    setAddedCount(prev => prev + 1);
    setAddedIndices(prev => new Set([...prev, index]));
    setSuccessMessage(`"${attraction.name}" added to your trip!`);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleReplace = (attraction, index, stopName, dayNum) => {
    if (hasReachedLimit && !addedIndices.has(index)) return;
    setAddedCount(prev => prev + (addedIndices.has(index) ? 0 : 1));
    setAddedIndices(prev => new Set([...prev, index]));
    setReplacingIndex(null);
    setSuccessMessage(`Replaced "${stopName}" with "${attraction.name}" on Day ${dayNum}!`);
    setTimeout(() => setSuccessMessage(null), 3000);
  };

  const handleToggleReplace = (index) => {
    setReplacingIndex(index === null ? null : (replacingIndex === index ? null : index));
  };

  if (attractions.length === 0) {
    return (
      <div className="text-center py-8 bg-neutral-50 rounded-xl border-2 border-dashed border-neutral-300">
        <p className="text-neutral-600">No attractions with coordinates found in this sample.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h3 className="text-2xl font-bold text-neutral-800 flex items-center gap-3 pb-3 border-b border-neutral-300">
        <div className="w-10 h-10 bg-gradient-to-br from-purple-400 to-purple-600 rounded-full flex items-center justify-center">
          <FaLandmark className="text-white text-xl" />
        </div>
        Attractions
      </h3>

      {/* Success message */}
      {successMessage && (
        <div className="flex items-center gap-2 p-3 rounded-lg border bg-green-50 border-green-200 animate-slide-down">
          <FaCheck className="text-green-500" />
          <p className="text-sm font-medium text-green-700">{successMessage}</p>
        </div>
      )}

      <div className={`flex items-center justify-between p-3 rounded-lg border ${hasReachedLimit ? 'bg-amber-50 border-amber-200' : 'bg-purple-50 border-purple-200'}`}>
        <p className={`text-sm font-medium ${hasReachedLimit ? 'text-amber-700' : 'text-purple-700'}`}>
          {hasReachedLimit
            ? 'Demo limit reached for adding attractions. Subscribe to Solo plan for unlimited.'
            : `Demo mode: ${addedCount}/${DEMO_ATTRACTION_ADD_LIMIT} attractions added (local only)`}
        </p>
        {hasReachedLimit && onGetStarted && (
          <button onClick={onGetStarted} className="px-3 py-1.5 text-xs font-semibold bg-amber-500 text-white rounded-lg hover:bg-amber-600 transition-colors">
            Upgrade Plan
          </button>
        )}
      </div>

      {attractions.map((attraction, index) => (
        <DemoAttractionCard
          key={index}
          attraction={attraction}
          index={index}
          destination={destination}
          onGetStarted={onGetStarted}
          onAdd={handleAdd}
          onToggleReplace={handleToggleReplace}
          isPickingReplace={replacingIndex === index}
          itineraryDays={itineraryDays}
          onReplace={handleReplace}
          isAdded={addedIndices.has(index)}
          hasReachedLimit={hasReachedLimit}
        />
      ))}
    </div>
  );
};

const SampleItinerary = ({ slug, onBack, onGetStarted }) => {
  const [itinerary, setItinerary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('itinerary');
  const [isGeneratingPDF, setIsGeneratingPDF] = useState(false);
  const [itineraryViewMode, setItineraryViewMode] = useState('cards');
  const [selectedDay, setSelectedDay] = useState(null);

  // Checklist state
  const checklistKey = `demo-checklist-${slug}`;
  const [checkedItems, setCheckedItems] = useState(() => {
    try {
      const saved = localStorage.getItem(checklistKey);
      return saved ? JSON.parse(saved) : {};
    } catch { return {}; }
  });
  const [expandedSections, setExpandedSections] = useState(new Set());

  const toggleChecklistItem = (itemKey) => {
    setCheckedItems(prev => {
      const updated = { ...prev, [itemKey]: !prev[itemKey] };
      if (!updated[itemKey]) delete updated[itemKey];
      localStorage.setItem(checklistKey, JSON.stringify(updated));
      return updated;
    });
  };

  useEffect(() => {
    const loadSample = async () => {
      const sampleConfig = SAMPLE_SLUGS[slug];
      if (!sampleConfig) {
        setError('Sample itinerary not found');
        setLoading(false);
        return;
      }

      try {
        const response = await fetch(sampleConfig.file);
        if (!response.ok) throw new Error('Failed to load sample');
        const data = await response.json();
        setItinerary(data);
      } catch (err) {
        console.error('Error loading sample:', err);
        setError('Failed to load sample itinerary');
      } finally {
        setLoading(false);
      }
    };

    loadSample();
    window.scrollTo(0, 0);
  }, [slug]);

  const handleDownloadPDF = () => {
    if (!itinerary) return;
    try {
      setIsGeneratingPDF(true);
      generateItineraryPDF(itinerary, { userTier: 'demo', isDemo: true });
    } catch (err) {
      console.error('PDF error:', err);
    } finally {
      setIsGeneratingPDF(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-neutral-600 font-medium text-lg">Loading sample itinerary...</p>
        </div>
      </div>
    );
  }

  if (error || !itinerary) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-50">
        <div className="text-center">
          <p className="text-red-600 font-medium text-lg mb-4">{error || 'Sample not found'}</p>
          <button onClick={onBack} className="px-6 py-3 bg-primary-500 text-white rounded-lg font-semibold hover:bg-primary-600">
            Go Back
          </button>
        </div>
      </div>
    );
  }

  const {
    start_location, end_location, interests, food_preference,
    start_date: storedStartDate, end_date: storedEndDate,
    transportation_mode, itinerary: content, member_count = 1
  } = itinerary;

  // Everything downstream — the day cards' per-night check-in dates, the flight
  // block, the dates shown to the reader — uses the rolled window, never the
  // stored one. A plain call, not a hook: the early returns above rule those out.
  const { startDate: start_date, endDate: end_date } = rollSampleDates(storedStartDate, storedEndDate);

  const { mainContent, checklistContent } = parseContentSections(content);
  const checklistSections = parseChecklistIntoSections(checklistContent);

  let itineraryDays = [];
  try {
    itineraryDays = content ? extractDaysFromContent(content) : [];
  } catch (e) {
    console.error('extractDaysFromContent failed:', e);
  }

  // Upsell banner component
  const UpgradePrompt = ({ feature }) => (
    <div className="bg-gradient-to-r from-amber-50 to-yellow-50 border border-amber-200 rounded-xl p-6 text-center">
      <FaCrown className="text-3xl text-amber-500 mx-auto mb-3" />
      <h4 className="font-bold text-neutral-800 text-lg mb-2">Subscribe to Solo plan for {feature}</h4>
      <p className="text-neutral-600 text-sm mb-4">Get unlimited access to all features starting at $7.99/month</p>
      <button
        onClick={onGetStarted}
        className="px-6 py-3 bg-gradient-to-r from-primary-500 to-accent-500 text-white rounded-xl font-semibold hover:shadow-lg hover:scale-105 transition-all"
      >
        Get Started - It's Free to Try
      </button>
    </div>
  );

  return (
    <div className="min-h-screen bg-neutral-50">
      {/* Demo Banner */}
      <div className="bg-gradient-to-r from-amber-400 via-amber-500 to-yellow-500 text-white py-3 px-4 text-center shadow-md sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <FaStar className="text-yellow-200" />
            <span className="font-semibold text-sm md:text-base">Sample Itinerary — Demo Mode</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-white/90 text-sm hidden sm:inline">Like what you see?</span>
            <button
              onClick={onGetStarted}
              className="px-4 py-1.5 bg-white text-amber-600 rounded-lg font-bold text-sm hover:bg-amber-50 transition-colors shadow-sm"
            >
              Sign Up Free
            </button>
            <button
              onClick={onBack}
              className="px-3 py-1.5 bg-white/20 text-white rounded-lg font-medium text-sm hover:bg-white/30 transition-colors flex items-center gap-1"
            >
              <FaArrowLeft className="text-xs" /> Back
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="bg-gradient-sunset rounded-2xl p-6 md:p-8 mb-8 text-white shadow-elevated">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold mb-2">Sample: {end_location}</h1>
              <p className="text-white/90">See what ItinerarySeek creates for you</p>
            </div>
            <div className="flex flex-wrap gap-2">
              <button
                onClick={handleDownloadPDF}
                disabled={isGeneratingPDF}
                className={`bg-white text-primary-600 px-4 py-2 rounded-lg font-medium flex items-center gap-2 transition-all ${
                  isGeneratingPDF ? 'opacity-50 cursor-not-allowed' : 'hover:shadow-lg'
                }`}
              >
                <FaFilePdf className={isGeneratingPDF ? 'animate-pulse' : ''} />
                {isGeneratingPDF ? 'Generating...' : 'PDF'}
              </button>
              <button
                onClick={onGetStarted}
                className="bg-white/20 backdrop-blur-sm text-white px-4 py-2 rounded-lg font-medium hover:bg-white/30 transition-colors"
              >
                Create Your Own
              </button>
            </div>
          </div>
        </div>

        {/* Tab Navigation */}
        <div className="mb-8">
          <div className="bg-neutral-200 px-2 pt-2 rounded-t-xl">
            <div className="flex flex-wrap">
              {[
                { id: 'summary', label: 'Summary', icon: FaGlobe, color: 'indigo' },
                { id: 'itinerary', label: 'Itinerary', icon: FaRoute, color: 'primary' },
                { id: 'flights', label: 'Flights', icon: FaPlane, color: 'sky' },
                { id: 'checklist', label: 'Checklist', icon: FaClipboardList, color: 'green' },
                { id: 'attractions', label: 'Attractions', icon: FaLandmark, color: 'purple' },
                { id: 'expenses', label: 'Expenses', icon: FaReceipt, color: 'amber' },
                { id: 'photos', label: 'Photos', icon: FaImage, color: 'pink' },
                { id: 'rating', label: 'Rate Trip', icon: FaStar, color: 'pink' },
              ].map(tab => (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center justify-center gap-2 px-5 py-3 font-semibold text-sm md:text-base transition-all ${
                    activeTab === tab.id
                      ? `text-${tab.color}-600 bg-white rounded-t-lg border-t-4 border-${tab.color}-500`
                      : `text-neutral-600 hover:text-${tab.color}-600`
                  }`}
                >
                  <tab.icon className="text-lg" />
                  <span className="hidden sm:inline">{tab.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-b-xl shadow-medium p-6">
            {/* Summary Tab */}
            {activeTab === 'summary' && (
              <div>
                <h2 className="text-xl font-bold text-neutral-800 mb-4 pb-3 border-b border-neutral-300 flex items-center gap-2">
                  <FaGlobe className="text-indigo-500" /> Trip Summary
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="flex items-start gap-3">
                    <FaMapMarkerAlt className="text-primary-500 text-xl mt-1" />
                    <div>
                      <p className="text-xs text-neutral-500 uppercase tracking-wide">Destination</p>
                      <p className="font-semibold text-neutral-800">{end_location}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <FaHeart className="text-primary-400 text-xl mt-1" />
                    <div>
                      <p className="text-xs text-neutral-500 uppercase tracking-wide mb-1">Interests</p>
                      <div className="flex flex-wrap gap-2">
                        {(typeof interests === 'string' ? interests.split(',') : interests || []).map((interest, idx) => (
                          <span key={idx} className="bg-primary-50 text-primary-700 px-2 py-1 rounded-md text-xs font-medium">
                            {interest.trim()}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                  {food_preference && (
                    <div className="flex items-start gap-3">
                      <FaUtensils className="text-accent-500 text-xl mt-1" />
                      <div>
                        <p className="text-xs text-neutral-500 uppercase tracking-wide">Food Preference</p>
                        <p className="font-semibold text-neutral-800">{food_preference}</p>
                      </div>
                    </div>
                  )}
                  {start_date && (
                    <div className="flex items-start gap-3">
                      <FaCalendarAlt className="text-accent-500 text-xl mt-1" />
                      <div>
                        <p className="text-xs text-neutral-500 uppercase tracking-wide">Travel Dates</p>
                        <p className="font-semibold text-neutral-800">{start_date}{end_date ? ` to ${end_date}` : ''}</p>
                      </div>
                    </div>
                  )}
                  {transportation_mode && (
                    <div className="flex items-start gap-3">
                      <FaCar className="text-secondary-500 text-xl mt-1" />
                      <div>
                        <p className="text-xs text-neutral-500 uppercase tracking-wide">Transportation</p>
                        <p className="font-semibold text-neutral-800">{transportation_mode}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* CTA in Summary */}
                <div className="mt-8">
                  <UpgradePrompt feature="personalized itineraries" />
                </div>
              </div>
            )}

            {/* Itinerary Tab */}
            {activeTab === 'itinerary' && (
              <div>
                {/* View Toggle */}
                {itineraryDays.length > 0 && (
                  <div className="flex items-center justify-between mb-5 pb-4 border-b border-slate-200">
                    <div>
                      <h3 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
                        {itineraryViewMode === 'cards' ? <FaTh className="text-indigo-500" /> : <FaList className="text-indigo-500" />}
                        Your Journey
                      </h3>
                      <p className="text-sm text-slate-500 mt-0.5">
                        {itineraryDays.length} days · {itineraryDays.reduce((acc, d) => acc + (d.locations?.length || 0), 0)} stops
                      </p>
                    </div>
                    <div className="flex items-center gap-1 bg-slate-100 p-1 rounded-lg">
                      <button
                        onClick={() => setItineraryViewMode('cards')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                          itineraryViewMode === 'cards' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-600 hover:text-slate-800'
                        }`}
                      >
                        <FaTh className="text-xs" />
                        <span className="hidden sm:inline">Cards</span>
                      </button>
                      <button
                        onClick={() => setItineraryViewMode('detailed')}
                        className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium transition-all ${
                          itineraryViewMode === 'detailed' ? 'bg-white text-indigo-600 shadow-sm' : 'text-slate-600 hover:text-slate-800'
                        }`}
                      >
                        <FaList className="text-xs" />
                        <span className="hidden sm:inline">Detailed</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Card View */}
                {itineraryViewMode === 'cards' && itineraryDays.length > 0 && (
                  <DayCardsGrid
                    days={itineraryDays}
                    startDate={start_date}
                    weatherData={null}
                    attractionStatuses={{}}
                    onViewDay={(day) => setSelectedDay(day)}
                    onSwapDays={() => {}}
                    isSwapping={false}
                    isOwner={false}
                    userTier="individual"
                    onViewPricing={onGetStarted}
                    // Without this the day cards fall back to whatever follows
                    // the colon in the day title, and the hotel search is sent
                    // to Booking.com without a city.
                    destination={end_location}
                    bookingSurface={`sample_${slug}`}
                  />
                )}

                {/* Detailed View */}
                {(itineraryViewMode === 'detailed' || itineraryDays.length === 0) && (
                  <FormattedContent content={mainContent} />
                )}

                {/* Someone reading a full itinerary end to end is a strong
                    booking prospect: one flight search for the whole trip,
                    alongside the per-night hotel links on the day cards. */}
                <BookingBlock
                  destination={end_location}
                  surface={`sample_${slug}`}
                  heading={`Where to stay in ${end_location}`}
                  initialCheckIn={start_date}
                  initialCheckOut={end_date}
                  initialOrigin={start_location !== end_location ? start_location : ''}
                />

                {/* Day Detail Modal */}
                {selectedDay && (
                  <DayDetailModal
                    isOpen={selectedDay !== null}
                    onClose={() => setSelectedDay(null)}
                    day={selectedDay}
                    startDate={start_date}
                    weatherData={null}
                    attractionStatuses={{}}
                    onAttractionStatusUpdate={() => {}}
                    flights={[]}
                    isOwner={false}
                    destination={end_location}
                    bookingSurface={`sample_${slug}`}
                    onExplore={() => {}}
                  />
                )}

                {/* Translate/Day Swap upsell */}
                <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-200 flex items-center gap-3">
                    <FaGlobe className="text-2xl text-neutral-400" />
                    <div className="flex-1">
                      <p className="font-semibold text-neutral-700">Translate Itinerary</p>
                      <p className="text-xs text-neutral-500">Available in 15 languages</p>
                    </div>
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold flex items-center gap-1">
                      <FaLock className="text-[10px]" /> Solo Plan
                    </span>
                  </div>
                  <div className="bg-neutral-50 rounded-xl p-4 border border-neutral-200 flex items-center gap-3">
                    <FaRoute className="text-2xl text-neutral-400" />
                    <div className="flex-1">
                      <p className="font-semibold text-neutral-700">Swap Days</p>
                      <p className="text-xs text-neutral-500">Reorder your itinerary</p>
                    </div>
                    <span className="px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold flex items-center gap-1">
                      <FaLock className="text-[10px]" /> Solo Plan
                    </span>
                  </div>
                </div>
              </div>
            )}

            {/* Flights Tab */}
            {activeTab === 'flights' && (
              <DemoFlightManager startDate={start_date} endDate={end_date} onGetStarted={onGetStarted} />
            )}

            {/* Checklist Tab */}
            {activeTab === 'checklist' && (
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-neutral-800 flex items-center gap-3 pb-3 border-b border-neutral-300">
                  <div className="w-10 h-10 bg-gradient-to-br from-green-400 to-green-600 rounded-full flex items-center justify-center">
                    <FaClipboardList className="text-white text-xl" />
                  </div>
                  Packing Checklist
                </h3>

                <div className="p-3 rounded-lg border bg-green-50 border-green-200">
                  <p className="text-sm font-medium text-green-700">
                    Checklist works fully in demo mode — your progress is saved locally.
                  </p>
                </div>

                {checklistSections.length > 0 ? (
                  checklistSections.map((section, sIdx) => {
                    const isExpanded = expandedSections.has(section.title);
                    const checkedCount = section.items.filter((_, idx) => checkedItems[`${section.title}:${idx}`]).length;

                    return (
                      <div key={sIdx} className="bg-neutral-50 rounded-xl border border-neutral-200 overflow-hidden">
                        <div
                          className="flex items-center justify-between px-4 py-3 cursor-pointer hover:bg-neutral-100 transition-colors"
                          onClick={() => {
                            setExpandedSections(prev => {
                              const next = new Set(prev);
                              if (next.has(section.title)) next.delete(section.title);
                              else next.add(section.title);
                              return next;
                            });
                          }}
                        >
                          <div className="flex items-center gap-3">
                            {isExpanded ? <FaChevronDown className="text-neutral-400 text-sm" /> : <FaChevronRight className="text-neutral-400 text-sm" />}
                            <span className="font-semibold text-neutral-800">{section.title}</span>
                            <span className="text-xs bg-neutral-200 text-neutral-600 px-2 py-0.5 rounded-full">{checkedCount}/{section.items.length}</span>
                          </div>
                        </div>
                        {isExpanded && (
                          <div className="px-4 pb-3 space-y-1">
                            {section.items.map((item, idx) => {
                              const itemKey = `${section.title}:${idx}`;
                              const isChecked = !!checkedItems[itemKey];
                              return (
                                <div key={itemKey} className="flex items-center gap-3 py-1.5 px-3 rounded-lg hover:bg-white group transition-colors">
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={() => toggleChecklistItem(itemKey)}
                                    className="w-4 h-4 text-green-600 rounded border-neutral-300 focus:ring-green-500 cursor-pointer flex-shrink-0"
                                  />
                                  <span className={`flex-1 text-sm ${isChecked ? 'line-through text-neutral-400' : 'text-neutral-700'}`}>{item}</span>
                                </div>
                              );
                            })}
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-8 bg-neutral-50 rounded-xl border-2 border-dashed border-neutral-300">
                    <p className="text-neutral-600">No checklist available for this sample.</p>
                  </div>
                )}
              </div>
            )}

            {/* Attractions Tab */}
            {activeTab === 'attractions' && (
              <DemoAttractionsTab content={content} destination={end_location} onGetStarted={onGetStarted} itineraryDays={itineraryDays} />
            )}

            {/* Expenses Tab */}
            {activeTab === 'expenses' && (
              <DemoExpenses destination={end_location} onGetStarted={onGetStarted} />
            )}

            {/* Photos Tab */}
            {activeTab === 'photos' && (
              <DemoPhotoGallery />
            )}

            {/* Rating Tab */}
            {activeTab === 'rating' && (
              <div className="space-y-4">
                <h3 className="text-2xl font-bold text-neutral-800 flex items-center gap-3 pb-3 border-b border-neutral-300">
                  <div className="w-10 h-10 bg-gradient-to-br from-pink-400 to-pink-600 rounded-full flex items-center justify-center">
                    <FaStar className="text-white text-xl" />
                  </div>
                  Rate This Trip
                </h3>

                <div className="p-3 rounded-lg border bg-neutral-100 border-neutral-200">
                  <p className="text-sm font-medium text-neutral-600 flex items-center gap-2">
                    <FaLock className="text-neutral-400" />
                    Rating is disabled in demo mode. Create your own itinerary to rate your trips!
                  </p>
                </div>

                <UpgradePrompt feature="trip ratings and feedback" />
              </div>
            )}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-8 bg-gradient-to-r from-primary-600 via-accent-600 to-adventure-600 rounded-2xl p-8 text-white text-center shadow-elevated">
          <h2 className="text-2xl md:text-3xl font-bold mb-3">Ready to Plan Your Own Trip?</h2>
          <p className="text-white/90 mb-6 max-w-lg mx-auto">
            Create a personalized itinerary for any destination in seconds.
          </p>
          <button
            onClick={onGetStarted}
            className="px-10 py-4 bg-white text-primary-600 rounded-2xl font-bold text-lg hover:shadow-lg hover:scale-105 transition-all"
          >
            Create My Free Itinerary
          </button>
        </div>
      </div>

      {/* Demo Chatbot */}
      <DemoChatbot destination={end_location} />
    </div>
  );
};

export { SAMPLE_SLUGS };
export default SampleItinerary;
